import { useState, useEffect, useCallback } from 'react';

// ── URL ↔ page key mapping ────────────────────────────────────────────────────
const PATH_TO_PAGE = {
  '/':              'home',
  '/home':          'home',
  '/checklist':     'checklist',
  '/stocktake':     'stocktake',
  '/orders':        'orders',
  '/transfers':     'transfers',
  '/allergens':     'allergens',
  '/training':      'training',
  '/calendar':      'calendar',
  '/reports':       'reports',
  '/results':       'results',
  '/group-results': 'group-results',
  '/governance':    'governance',
  '/shifty':        'shifty',
  '/team':          'team',
  '/docs':          'docs',
  '/maintenance':   'maintenance',
  '/data-entry':    'data-entry',
  '/checklists':    'checklists',
  '/fridgetemps':   'fridgetemps',
  '/products':      'products',
  '/product-order': 'product-order',
  '/users':         'users',
  '/settings':      'settings',
  '/events':        'events',
};

const PAGE_TO_PATH = Object.fromEntries(
  Object.entries(PATH_TO_PAGE).map(([path, page]) => [page, path])
);

// Paths handled outside the router — don't intercept these
const BYPASS_PATTERNS = [
  /^\/onboarding\//,
  /^\/welcome\//,
  /^\/balance\//,
  /^\/set-password/,
  /^\/event-enquiry/,
  /^\/governance\/share/,
];

function shouldBypass(pathname) {
  return BYPASS_PATTERNS.some(re => re.test(pathname));
}

function getPageFromPath(pathname) {
  if (shouldBypass(pathname)) return null;
  return PATH_TO_PAGE[pathname] ?? null;
}

/**
 * useRouter — drop-in replacement for `activePage` / `setActivePage`.
 *
 * Returns:
 *   activePage  — current page key string (or null before auth + default resolves)
 *   navigate    — stable fn (pageKey: string) => void, safe to pass as onNavigate
 *   hasUrlIntent — true if the user landed on a specific URL (not just '/')
 *                  App uses this to override the role-based default landing page
 */
export function useRouter() {
  const initialPage = getPageFromPath(window.location.pathname);

  const [activePage, setActivePageState] = useState(initialPage);

  // Sync browser back / forward buttons
  useEffect(() => {
    function handlePop() {
      const fromPath = getPageFromPath(window.location.pathname);
      if (fromPath !== null) {
        setActivePageState(fromPath);
      }
    }
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const navigate = useCallback((pageKey) => {
    const path = PAGE_TO_PATH[pageKey];
    if (path && window.location.pathname !== path) {
      window.history.pushState({ pageKey }, '', path);
    }
    setActivePageState(pageKey);
  }, []);

  // True when the user arrived at a real page URL (not root / or unknown)
  const hasUrlIntent = initialPage !== null;

  return { activePage, navigate, hasUrlIntent };
}